/**
 * The case record, as an engineer actually edits it.
 *
 * Replaces the four plain text boxes of TrailSteps' `CaseFields` on the review
 * screen with the shape the schema grew in migration 008:
 *
 *   PROBLEM      one box
 *   SYMPTOMS     a list — what was seen
 *   FACTS        a list — what was stated
 *   CAUSES       ONE list, two states (active / ruled out)
 *   RESOLUTION   one box
 *
 * Why a new file rather than another branch inside `CaseFields`: that component
 * lives in a 1000-line module three screens import, and none of this belongs to
 * the other two.
 *
 * ── The two rules this file exists to enforce ────────────────────────────────
 *
 * 1. **An empty reason is not a ruling-out.** "Nobody tried it" and "we proved
 *    it was not this" are opposite evidence. A cause nobody tested IS still in
 *    play — it has not been disproved, merely unexamined. The original ask was
 *    two states: "either ruled out, excluded, or included".
 *
 *    Asking to rule a cause out opens the reason line, but the row only
 *    *becomes* `ruled_out` once that line has text in it — and goes straight
 *    back to `active` if the text is cleared. `causesToPatch` filters again on
 *    the way out, so nothing can reach the wire as a reasonless exclusion.
 *
 * 2. **The root cause is picked, never typed.** One click promotes an `active`
 *    cause, which is how `root_cause_nid` stays a real node id. A cause the
 *    engineer typed in has no node id; it still works, it just carries an empty
 *    nid, and the row shows its id (or lack of one) so that is visible.
 *
 * ── Degrading ────────────────────────────────────────────────────────────────
 *
 * The v2 columns exist but are empty, and older trails will never have them.
 * Everything falls back to `symptom_text` / `possible_causes`, and every save
 * also writes those two legacy columns — the causes list round-trips through
 * `possible_causes` as prefixed lines, so both states and their reasons
 * survive a reload even on a backend that has not learned the new fields yet.
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Check, X, Plus, Crown, Copy, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import "./styles/informational.css";
// LearningEntries' `heading` mode uses the turn breakdown's section heading row.
import "./styles/turn-breakdown.css";

/* ── cause states ───────────────────────────────────────────────────────── */

export const ACTIVE = "active";
export const RULED_OUT = "ruled_out";

/**
 * Two states only. A cause nobody tested IS still in play — it has not been
 * disproved, merely unexamined. There is no third state and no column for one.
 */
export const CAUSE_STATES = [
  {
    value: ACTIVE,
    label: "Still in play",
    short: "Active",
    Icon: Check,
    on: "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200",
  },
  {
    value: RULED_OUT,
    label: "Ruled out — needs a reason",
    short: "Ruled out",
    Icon: X,
    on: "bg-rose-50 text-rose-600 ring-1 ring-rose-200",
  },
];

const STATE_SET = new Set([ACTIVE, RULED_OUT]);

/* ── tiny coercions (the API hands us nulls, strings and arrays) ─────────── */

export const asText = (v) => {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
};

const cleanList = (list) =>
  (Array.isArray(list) ? list : []).map((v) => asText(v).trim()).filter(Boolean);

/**
 * Defensively parse a value that MAY arrive as a JSON string instead of a
 * parsed object/array.  asyncpg on this project's shared pool hands back
 * jsonb columns as strings rather than parsed objects — `Array.isArray()`
 * returns false on a JSON string, silently dropping data.
 *
 * Returns the parsed value when it is already the right type or when
 * `JSON.parse` succeeds, otherwise returns `fallback`.
 */
export function safeJson(val, fallback = null) {
  if (val == null) return fallback;
  if (typeof val === "object") return val;          // already parsed
  if (typeof val !== "string") return fallback;
  try {
    const parsed = JSON.parse(val);
    return typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

/** Arrays arrive as TEXT[]; a null column and a missing column both read as []. */
const asList = (v) => (Array.isArray(v) ? v.map(asText) : []);

const norm = (s) => asText(s).trim().toLowerCase().replace(/\s+/g, " ");

let _seq = 0;
const nextId = () => `c${++_seq}`;

/* ── legacy `possible_causes` free text ↔ the two-state list ────────────── */

const GLYPH = { [ACTIVE]: "✓", [RULED_OUT]: "✗" };
const ROOT_SUFFIX = " (root cause)";
const WHY_SEP = " — "; // em dash, the separator the plan's mock-up uses

/**
 * Render the list back into the legacy free-text column.
 *
 * This is not decoration: until the v2 columns are written, this string is the
 * ONLY place the two states and their reasons survive a reload. It is parsed
 * back by `parseLegacyCauses`, so the two must stay symmetric.
 */
export function serializeLegacyCauses(causes = [], rootId = null) {
  return causes
    .map(normalizeCause)
    .filter((c) => c.label.trim())
    .map((c) => {
      const head = `${GLYPH[c.state]} ${c.label.trim()}`;
      if (c.state === RULED_OUT) return `${head}${WHY_SEP}${c.why.trim()}`;
      if (c.state === ACTIVE && c.id === rootId) return `${head}${ROOT_SUFFIX}`;
      return head;
    })
    .join("\n");
}

/**
 * Read the legacy column. Lines we wrote carry their state as a glyph; lines a
 * human (or an older extractor) wrote carry nothing and are taken at face value
 * as still-in-play — never as ruled out, which would invent evidence.
 */
export function parseLegacyCauses(text) {
  const out = [];
  let rootId = null;
  for (const raw of asText(text).split(/\r?\n/)) {
    let line = raw.trim().replace(/^[-*·•]\s*/, "").replace(/^\d+[.)]\s*/, "");
    if (!line) continue;
    let state = ACTIVE;
    if (line.startsWith(GLYPH[ACTIVE])) line = line.slice(1).trim();
    else if (line.startsWith(GLYPH[RULED_OUT])) {
      state = RULED_OUT;
      line = line.slice(1).trim();
    } else if (line.startsWith("?")) {
      // Legacy data: a "?" prefix used to mean "not tested". That state no
      // longer exists — a cause nobody tested is still in play (active).
      state = ACTIVE;
      line = line.slice(1).trim();
    }
    let isRoot = false;
    if (line.toLowerCase().endsWith(ROOT_SUFFIX)) {
      isRoot = true;
      line = line.slice(0, -ROOT_SUFFIX.length).trim();
    }
    let why = "";
    if (state === RULED_OUT) {
      const at = line.indexOf(WHY_SEP.trim());
      if (at > -1) {
        why = line.slice(at + 1).trim();
        line = line.slice(0, at).trim();
      }
    }
    if (!line) continue;
    const cause = normalizeCause({ id: nextId(), label: line, nid: "", state, why });
    if (isRoot && cause.state === ACTIVE) rootId = cause.id;
    out.push(cause);
  }
  return { causes: out, rootId };
}

/* ── the state machine ──────────────────────────────────────────────────── */

/**
 * The invariant, applied to one row: `ruled_out` with nothing in `why` is not
 * ruled out — a cause nobody tested is still in play. Every constructor and
 * every mutator runs through here, and so does the serialiser, so there is no
 * path that skips it.
 */
export function normalizeCause(c = {}) {
  const state = STATE_SET.has(c.state) ? c.state : ACTIVE;
  const why = asText(c.why);
  return {
    id: c.id || nextId(),
    label: asText(c.label),
    nid: asText(c.nid),
    why,
    state: state === RULED_OUT && !why.trim() ? ACTIVE : state,
    // UI only: the reason line is open because the engineer asked to rule this
    // out. Never serialised.
    askWhy: !!c.askWhy || (state === RULED_OUT && !!why.trim()),
  };
}

/** Move one cause to a state. Asking for `ruled_out` opens the reason line. */
export function applyCauseState(cause, next) {
  if (!STATE_SET.has(next)) return cause;
  if (next === RULED_OUT) {
    return normalizeCause({ ...cause, state: RULED_OUT, askWhy: true });
  }
  return normalizeCause({ ...cause, state: next, askWhy: false });
}

/** Type in the reason line: text promotes to ruled out, clearing it demotes. */
export function setCauseWhy(cause, text) {
  const why = asText(text);
  const asking = cause.askWhy || cause.state === RULED_OUT;
  if (!asking) return normalizeCause({ ...cause, why });
  return normalizeCause({ ...cause, why, state: RULED_OUT, askWhy: true });
}

/* ── trail → record ─────────────────────────────────────────────────────── */

export const EMPTY_RECORD = Object.freeze({
  intent: undefined, // "informational" | undefined (diagnostic)
  problem: "",
  symptoms: [],
  facts: [],
  causes: [],
  rootId: null,
  // The stored root cause when it does NOT map to an active row in the list —
  // the extractor is allowed to name the same string as both `root_cause` and
  // a ruled-out entry. Held so a save that touches something else cannot write
  // an empty string over a conclusion the engineer never disagreed with, and
  // dropped the moment the engineer unpicks the root cause themselves.
  rootFallback: null,
  // Job A writes still_open on DIAGNOSTIC sessions (the asks nobody answered),
  // so the diagnostic record carries it too — not only the informational one.
  still_open: [],
  resolution: "",
  summary: "",
});

export const EMPTY_RECORD_INFORMATIONAL = Object.freeze({
  intent: "informational",
  // Not rendered for this intent, but the component body reads them before the
  // intent branch — keep them present and empty so nothing has to guess.
  causes: [],
  rootId: null,
  about: "",
  key_takeaways: [],
  applies_to: [],
  valid_as_of: "",
  watch_out: [],
  still_open: [],
  entries: [],
});

/**
 * Build the editable record from whatever the API returned.
 *
 * v2 columns win; the legacy pair fills the gap so a trail captured before any
 * of this existed still opens with something on screen.
 */
/**
 * Build the editable record for an informational trail.
 */
/**
 * Coerce an entry's inner fields that may arrive as JSON strings from asyncpg.
 * Every array/object inside an entry goes through `safeJson` so downstream
 * rendering never sees a raw string where it expects an array.
 */
function normalizeEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const e = typeof raw === "string" ? safeJson(raw) : raw;
  if (!e || typeof e !== "object") return null;
  return {
    id: e.id ?? null,
    content_key: e.content_key ?? null,
    ordinal: e.ordinal ?? null,
    title: asText(e.title),
    kind: asText(e.kind),
    // turns arrives as an INT ARRAY (e.g. [1,2]) — tolerate a JSON string.
    turns: Array.isArray(e.turns) ? e.turns : (safeJson(e.turns, null) || []),
    lead: asText(e.lead),
    key_points: cleanList(Array.isArray(e.key_points) ? e.key_points : safeJson(e.key_points, []) || []),
    procedure: Array.isArray(e.procedure) ? e.procedure : safeJson(e.procedure, []) || [],
    options: Array.isArray(e.options) ? e.options : safeJson(e.options, []) || [],
    verification: cleanList(Array.isArray(e.verification) ? e.verification : safeJson(e.verification, []) || []),
    applies_to: cleanList(Array.isArray(e.applies_to) ? e.applies_to : safeJson(e.applies_to, []) || []),
    prerequisites: cleanList(Array.isArray(e.prerequisites) ? e.prerequisites : safeJson(e.prerequisites, []) || []),
    caveats: cleanList(Array.isArray(e.caveats) ? e.caveats : safeJson(e.caveats, []) || []),
    sources: Array.isArray(e.sources) ? e.sources : safeJson(e.sources, []) || [],
    asked_as: asText(e.asked_as),
    corrected: e.corrected != null ? (typeof e.corrected === "object" ? e.corrected : safeJson(e.corrected)) : null,
    meta: e.meta != null ? (typeof e.meta === "object" ? e.meta : safeJson(e.meta)) : null,
    // Build good_to_know from the entry-level fields if not already present.
    good_to_know: buildGoodToKnow(e),
  };
}

/** Compose the per-entry good_to_know block from either nested or flat fields. */
function buildGoodToKnow(e) {
  // If it is already a nested object, parse defensively and return.
  const existing = e.good_to_know != null
    ? (typeof e.good_to_know === "object" ? e.good_to_know : safeJson(e.good_to_know))
    : null;
  if (existing && typeof existing === "object") {
    return {
      applies_to: cleanList(Array.isArray(existing.applies_to) ? existing.applies_to : safeJson(existing.applies_to, []) || []),
      prerequisites: cleanList(Array.isArray(existing.prerequisites) ? existing.prerequisites : safeJson(existing.prerequisites, []) || []),
      caveats: cleanList(Array.isArray(existing.caveats) ? existing.caveats : safeJson(existing.caveats, []) || []),
    };
  }
  // Fall back to flat entry-level fields (the API contract puts them on the entry).
  const at = cleanList(Array.isArray(e.applies_to) ? e.applies_to : safeJson(e.applies_to, []) || []);
  const pre = cleanList(Array.isArray(e.prerequisites) ? e.prerequisites : safeJson(e.prerequisites, []) || []);
  const cav = cleanList(Array.isArray(e.caveats) ? e.caveats : safeJson(e.caveats, []) || []);
  if (!at.length && !pre.length && !cav.length) return null;
  return { applies_to: at, prerequisites: pre, caveats: cav };
}

export function recordFromTrailInformational(trail) {
  if (!trail) return { ...EMPTY_RECORD_INFORMATIONAL };
  // entries may arrive as a JSON string from asyncpg.
  const rawEntries = Array.isArray(trail.entries)
    ? trail.entries
    : safeJson(trail.entries, []) || [];
  const entries = rawEntries
    .map(normalizeEntry)
    .filter(Boolean);
  return {
    intent: "informational",
    causes: [],
    rootId: null,
    about: asText(trail.about),
    key_takeaways: cleanList(Array.isArray(trail.key_takeaways) ? trail.key_takeaways : safeJson(trail.key_takeaways, []) || []),
    applies_to: cleanList(Array.isArray(trail.applies_to) ? trail.applies_to : safeJson(trail.applies_to, []) || []),
    valid_as_of: asText(trail.valid_as_of),
    watch_out: cleanList(Array.isArray(trail.watch_out) ? trail.watch_out : safeJson(trail.watch_out, []) || []),
    still_open: cleanList(Array.isArray(trail.still_open) ? trail.still_open : safeJson(trail.still_open, []) || []),
    entries,
  };
}

export function recordFromTrail(trail, { problemFallback = true } = {}) {
  if (!trail) return { ...EMPTY_RECORD };
  if (trail.intent === "informational") return recordFromTrailInformational(trail);

  /* `problemFallback: false` is a DISPLAY gate only: the caller rendering an
     informational session in this diagnostic layout has no problem definition
     by construction, and symptom_text is just the opening message echoed. The
     trail itself is untouched, so symptom_text still reaches the save path. */
  const problem = asText(trail.problem_definition).trim()
    ? asText(trail.problem_definition)
    : problemFallback ? asText(trail.symptom_text) : "";

  const symptoms = cleanList(asList(trail.symptoms));
  const facts = cleanList(asList(trail.facts));

  const active = cleanList(asList(trail.possible_causes_active));
  const ruled = (Array.isArray(trail.possible_causes_ruled_out)
    ? trail.possible_causes_ruled_out
    : []
  )
    .map((r) =>
      r && typeof r === "object"
        ? { cause: asText(r.cause), why: asText(r.why) }
        : { cause: asText(r), why: "" }
    )
    .filter((r) => r.cause.trim());

  let causes = [];
  let rootId = null;
  if (active.length || ruled.length) {
    causes = [
      ...active.map((label) => normalizeCause({ label, state: ACTIVE })),
      // A stored entry with no reason is data that should never have been
      // written — normalizeCause sends it back to active rather than trusting it.
      ...ruled.map((r) =>
        normalizeCause({ label: r.cause, why: r.why, state: RULED_OUT })
      ),
    ];
  } else {
    const parsed = parseLegacyCauses(trail.possible_causes);
    causes = parsed.causes;
    rootId = parsed.rootId;
  }

  // The node id is the thing worth keeping, so it wins over the label text.
  const rootNid = asText(trail.root_cause_nid).trim();
  const rootLabel = norm(trail.root_cause);
  let rootFallback = null;
  if (rootNid || rootLabel) {
    const hit =
      (rootNid && causes.find((c) => c.nid === rootNid)) ||
      (rootLabel && causes.find((c) => norm(c.label) === rootLabel));
    if (hit) {
      // The stored node id belongs to the cause it names. `possible_causes_active`
      // is a TEXT[] and carries no ids, so the matched row has an empty nid —
      // leaving it that way is what used to blank `root_cause_nid` on the very
      // next save, and blank still satisfies the partial index.
      if (rootNid && !hit.nid) {
        causes = causes.map((c) => (c.id === hit.id ? { ...c, nid: rootNid } : c));
      }
      if (hit.state === ACTIVE) {
        rootId = hit.id;
      } else {
        // The stored root cause names a RULED-OUT row. That is the extractor's
        // conclusion, not a contradiction to resolve here: keep it verbatim so
        // the patch carries it back unchanged instead of erasing it.
        rootFallback = { label: asText(trail.root_cause).trim() || hit.label, nid: rootNid };
      }
    } else if (asText(trail.root_cause).trim()) {
      // Named a root cause that is not in the list: add it rather than drop it.
      const added = normalizeCause({
        label: asText(trail.root_cause),
        nid: rootNid,
        state: ACTIVE,
      });
      causes = [added, ...causes];
      rootId = added.id;
    } else if (rootNid) {
      // A node id with no label and nothing to hang it on. Still the stored
      // conclusion — held so the next save does not clear it.
      rootFallback = { label: "", nid: rootNid };
    }
  }

  return {
    problem,
    symptoms,
    facts,
    causes,
    rootId,
    rootFallback,
    still_open: cleanList(Array.isArray(trail.still_open) ? trail.still_open : safeJson(trail.still_open, []) || []),
    resolution: asText(trail.resolution),
    summary: asText(trail.summary),
  };
}

/**
 * Is there anything on this record worth reading?
 *
 * Drives the opening mode: previewing nothing is useless, so a trail that has
 * not been extracted yet opens in Edit. A cause with a blank label does not
 * count — an empty row the engineer added is still an empty record.
 */
export function isRecordEmpty(record) {
  if (!record) return true;
  if (record.intent === "informational") {
    return (
      !asText(record.about).trim() &&
      !cleanList(record.key_takeaways).length &&
      !(record.entries || []).length
    );
  }
  return (
    !asText(record.problem).trim() &&
    !cleanList(record.symptoms).length &&
    !cleanList(record.facts).length &&
    !(record.causes || []).some((c) => asText(c?.label).trim()) &&
    !cleanList(record.still_open).length &&
    !asText(record.resolution).trim() &&
    !asText(record.summary).trim()
  );
}

/* ── preview layout: which groups can share a row ───────────────────────── */

/** Prose wraps at roughly this many characters in half of the left pane. */
const PREVIEW_WRAP = 52;

/** Roughly how many lines a preview group takes. Close enough to compare two. */
export function previewLines(value) {
  if (Array.isArray(value)) return cleanList(value).length;
  return asText(value)
    .trim()
    .split(/\r?\n/)
    .filter(Boolean)
    .reduce((n, line) => n + Math.ceil(line.length / PREVIEW_WRAP), 0);
}

/** How far apart two groups may be before sharing a row wastes more than it saves. */
export const PAIR_SLACK = 2;

/**
 * Side by side, or one under the other?
 *
 * A fixed 50/50 split is the layout that looks densest and wastes the most:
 * a one-line SYMPTOMS beside a five-line FACTS leaves a column of nothing as
 * tall as four lines, and the eye reads that hole as a bug. Stacking costs a
 * line or two of height but never leaves a hole, so the pair only survives
 * while the two sides are within `PAIR_SLACK` lines of each other.
 *
 * Two empty groups pair — "Not filled in." twice is one line each.
 */
export function pairsWell(a, b) {
  return Math.abs(previewLines(a) - previewLines(b)) <= PAIR_SLACK;
}

/* ── record → PATCH ─────────────────────────────────────────────────────── */

/** The keys this backend has always accepted — the degraded retry set. */
export const LEGACY_RECORD_KEYS = new Set([
  "symptom_text",
  "possible_causes",
  "resolution",
  "summary",
]);

/** Which PATCH keys each editable section owns — DIAGNOSTIC. */
export const SECTION_KEYS = {
  problem: ["problem_definition", "symptom_text"],
  symptoms: ["symptoms"],
  facts: ["facts"],
  causes: [
    "possible_causes_active",
    "possible_causes_ruled_out",
    "possible_causes",
    "root_cause",
    "root_cause_nid",
  ],
  root: ["root_cause", "root_cause_nid", "possible_causes"],
  resolution: ["resolution"],
  summary: ["summary"],
  // ── informational synthesis fields (identity mappings) ──
  about: ["about"],
  key_takeaways: ["key_takeaways"],
  applies_to: ["applies_to"],
  valid_as_of: ["valid_as_of"],
  watch_out: ["watch_out"],
  still_open: ["still_open"],
};

/** The causes half of the patch. Exported because it is what the rule lives in. */
export function causesToPatch(causes = [], rootId = null, rootFallback = null) {
  const list = causes.map(normalizeCause).filter((c) => c.label.trim());
  const root = list.find((c) => c.id === rootId && c.state === ACTIVE) || null;
  // The root cause the record currently stands behind: the picked row if there
  // is one, otherwise whatever was stored and never contradicted.
  const rootText = root ? root.label.trim() : asText(rootFallback?.label).trim();
  const rootNid = root ? root.nid.trim() : asText(rootFallback?.nid).trim();
  const patch = {
    // Causes that are not ruled out all go into active — there is no third
    // state and no column for one. A cause nobody tested is still in play.
    possible_causes_active: list
      .filter((c) => c.state !== RULED_OUT)
      .map((c) => c.label.trim()),
    // Belt and braces: the state machine already guarantees it, and this
    // guarantees it again at the only point that touches the wire.
    possible_causes_ruled_out: list
      .filter((c) => c.state === RULED_OUT && c.why.trim())
      .map((c) => ({ cause: c.label.trim(), why: c.why.trim() })),
    possible_causes: serializeLegacyCauses(list, root ? root.id : null),
    // An empty `root_cause` is a real instruction — the engineer unpicked it —
    // so it is still sent; `recordToPatch` drops it again when it matches what
    // was loaded, which is what keeps `root_cause_edited_at` unstamped.
    root_cause: rootText,
  };
  // NEVER "": the empty string still satisfies the partial index
  // `WHERE root_cause_nid IS NOT NULL`, so every blanked trail would collapse
  // into one empty-string group and poison the strength ranking. No real id
  // means the key does not travel at all.
  if (rootNid) patch.root_cause_nid = rootNid;
  return patch;
}

/** Deep-ish equality for the value shapes a patch carries (strings, arrays). */
const sameValue = (a, b) =>
  typeof a === "string" && typeof b === "string" ? a === b : JSON.stringify(a) === JSON.stringify(b);

/**
 * The whole record as a PATCH body.
 *
 * `sections` limits it to the blocks the engineer touched; `baseline` — the
 * record as it was LOADED — limits it again to the keys whose value actually
 * differs. Both matter: a section owns several keys, and the backend stamps a
 * per-field `*_edited_at` marker for every key it receives, which locks
 * extraction out of that field forever. Renaming one cause must not cost the
 * trail its extracted root cause.
 */
export function recordToPatch(record, sections = null, baseline = null) {
  /* Branch on intent so an informational record never computes diagnostic
     fields (causesToPatch, symptom_text mirror, etc.) and vice versa. If the
     two sets were merged, a dirty informational section would carry empty
     diagnostic columns in the patch and blank them on a mixed session. */
  const full = record.intent === "informational"
    ? {
        about: asText(record.about),
        key_takeaways: cleanList(record.key_takeaways),
        applies_to: cleanList(record.applies_to),
        valid_as_of: asText(record.valid_as_of),
        watch_out: cleanList(record.watch_out),
        still_open: cleanList(record.still_open),
      }
    : {
        problem_definition: asText(record.problem),
        // Legacy mirror: MyTrails, the review header and getSessionSymptom all
        // still read symptom_text. Writing only problem_definition would leave the
        // trail list showing yesterday's text.
        symptom_text: asText(record.problem),
        symptoms: cleanList(record.symptoms),
        facts: cleanList(record.facts),
        ...causesToPatch(record.causes, record.rootId, record.rootFallback),
        still_open: cleanList(record.still_open),
        resolution: asText(record.resolution),
        summary: asText(record.summary),
      };
  let out = full;
  if (sections) {
    const wanted = new Set();
    for (const s of sections) (SECTION_KEYS[s] || []).forEach((k) => wanted.add(k));
    out = Object.fromEntries(Object.entries(full).filter(([k]) => wanted.has(k)));
  }
  if (!baseline) return out;
  const was = recordToPatch(baseline);
  return Object.fromEntries(
    Object.entries(out).filter(([k, v]) => !(k in was) || !sameValue(was[k], v))
  );
}

/* ── cause suggestions from the walk ────────────────────────────────────── */

const CAUSE_PREFIX = /^(PC|CAUSE|POSSIBLE_CAUSE|POSSIBLECAUSE)_/i;

/** "PC_MTU_MISMATCH" → "MTU Mismatch". Short words stay upper — they are acronyms. */
export function prettyNid(nid) {
  const bare = asText(nid).replace(CAUSE_PREFIX, "").replace(/[_-]+/g, " ").trim();
  if (!bare) return "";
  return bare
    .split(/\s+/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join(" ");
}

/* A runbook NODE ID token: uppercase letters followed by an index — PC2, RES12,
 * WA6B, Q3. The browser-side twin of `extract._NID_TOKEN_RE`, kept to the same
 * shape (uppercase only, so "IPv6" and "ZTunnel" stay words). */
const NID_TOKEN = /^[A-Z]{1,12}\d[0-9A-Z]*$/;
const MAX_NID_WORDS = 4;

/** Is this a bare node id dressed up as a label rather than a stated cause?
 *
 * Live data: the chip row offered "Ziazcc PC2" — `prettyNid("ZIAZCC_PC2")`,
 * which is a graph coordinate and not a hypothesis about anything. Clicking it
 * wrote a permanent search key that can never match a future case. */
export function looksLikeNid(text) {
  const raw = asText(text).trim();
  if (!raw) return false;
  const tokens = raw.split(/[\s_]+/).filter(Boolean);
  if (!tokens.length || tokens.length > MAX_NID_WORDS) return false;
  return tokens.some((t) => NID_TOKEN.test(t));
}

/* An MCQ answer, not a cause. The engine's branch conditions ARE the option
 * labels the engineer clicked — "Yes, ZTunnel 2.0 DTLS", "No improvement",
 * "Yes, OS was upgraded" — and rule 4 of the extractor says in as many words
 * that an answer is an observation, never a hypothesis about WHY. The backend
 * filters these out of `possible_causes_active`; the chips are built here, from
 * `edge_label`, so the same rule has to exist here too or the filtered text
 * walks back in through the one-click path. */
const ANSWER_LEAD =
  /^(yes|no|n\/a|na|none|maybe|unknown|not sure|don'?t know|i don'?t know|other|skip)\b/i;

/** Is this edge label the engineer's ANSWER rather than a cause? */
export function looksLikeAnswer(text) {
  return ANSWER_LEAD.test(asText(text).trim());
}

/** A usable chip label for a cause node, or "" when nothing here is one.
 *
 * The edge label is preferred — it is the engineer-readable branch condition —
 * but only when it is not an answer; otherwise the node's own prettified id is
 * tried, and that is rejected when it comes out as a coordinate. */
export function causeChipLabel(step) {
  const nid = asText(step?.to_nid).trim();
  const edge = asText(step?.edge_label).trim();
  if (edge && !looksLikeAnswer(edge) && !looksLikeNid(edge)) return edge;
  const pretty = prettyNid(nid);
  if (pretty && !looksLikeAnswer(pretty) && !looksLikeNid(pretty)) return pretty;
  return "";
}

/**
 * Causes the walk touched that are not on the list yet — offered as one-click
 * chips rather than being written in automatically. Nothing enters the record
 * without the engineer saying so, and a chip carries its node id, which is what
 * makes a picked root cause countable.
 *
 * SAME REJECT RULES AS THE EXTRACTOR. The stored causes are filtered server-side
 * (extract.py rule 4 + `_looks_like_nid`); these chips are built in the browser
 * from the walk, so they were not — and the row was still offering "Yes, ZTunnel
 * 2.0 DTLS", "Ziazcc PC2" and "Yes, OS was upgraded" as one-click causes after
 * the stored list had been cleaned. A chip is a WRITE the engineer makes with
 * one click, so it is held to the rules the write is held to.
 */
export function causeSuggestionsFromSteps(steps = [], causes = [], limit = 8) {
  const taken = new Set();
  for (const c of causes) {
    if (c.nid) taken.add(c.nid.toUpperCase());
    if (c.label) taken.add(norm(c.label));
  }
  const out = [];
  for (const s of Array.isArray(steps) ? steps : []) {
    const nid = asText(s?.to_nid).trim();
    if (!nid) continue;
    const isCause = s?.role === "cause" || CAUSE_PREFIX.test(nid);
    if (!isCause) continue;
    const label = causeChipLabel(s);
    if (!label) continue;
    if (taken.has(nid.toUpperCase()) || taken.has(norm(label))) continue;
    taken.add(nid.toUpperCase());
    taken.add(norm(label));
    out.push({ nid, label });
    if (out.length >= limit) break;
  }
  return out;
}

/* ── the ticket-shaped roll-up (the paste-into-a-ticket artefact) ────────── */

export function composeCaseText(record) {
  const parts = [];
  const push = (label, body) => {
    const v = asText(body).trim();
    if (v) parts.push(`${label}\n${v}`);
  };

  if (record.intent === "informational") {
    push("ABOUT", record.about);
    push("KEY TAKEAWAYS", cleanList(record.key_takeaways).map((s) => `- ${s}`).join("\n"));
    const gtkLines = [];
    const at = cleanList(record.applies_to);
    if (at.length) gtkLines.push(`Applies to: ${at.join(", ")}`);
    const vao = asText(record.valid_as_of).trim();
    if (vao) gtkLines.push(`Valid as of: ${vao}`);
    const wo = cleanList(record.watch_out);
    if (wo.length) gtkLines.push(`Careful: ${wo.join(", ")}`);
    if (gtkLines.length) parts.push(`GOOD TO KNOW\n${gtkLines.join("\n")}`);
    push("STILL OPEN", cleanList(record.still_open).map((s) => `- ${s}`).join("\n"));
    (record.entries || []).forEach((entry, i) => {
      const ep = [];
      ep.push(`${i + 1}. ${entry.title || "Untitled"}  [${(entry.kind || "").replace(/_/g, "-")}]`);
      if (entry.lead) ep.push(entry.lead);
      if ((entry.procedure || []).length)
        ep.push(entry.procedure.map((s) => `  ${s.n}. ${s.action}${s.note ? ` (${s.note})` : ""}`).join("\n"));
      if ((entry.key_points || []).length)
        ep.push(entry.key_points.map((s) => `  - ${s}`).join("\n"));
      if ((entry.options || []).length)
        ep.push(entry.options.map((o) => `  ${o.option}: ${o.when_to_use}${o.tradeoff ? ` (${o.tradeoff})` : ""}`).join("\n"));
      const gtk = entry.good_to_know || {};
      const eat = cleanList(gtk.applies_to);
      if (eat.length) ep.push(`  Applies to: ${eat.join(", ")}`);
      const pre = cleanList(gtk.prerequisites);
      if (pre.length) ep.push(`  Needs: ${pre.join(", ")}`);
      const cav = cleanList(gtk.caveats);
      if (cav.length) ep.push(cav.map((c) => `  ! ${c}`).join("\n"));
      if (entry.asked_as) ep.push(`  Asked as: "${entry.asked_as}"`);
      parts.push(ep.join("\n"));
    });
    return parts.join("\n\n");
  }

  push("PROBLEM", record.problem);
  push("SYMPTOMS", cleanList(record.symptoms).map((s) => `- ${s}`).join("\n"));
  push("FACTS", cleanList(record.facts).map((s) => `- ${s}`).join("\n"));
  const causes = record.causes.map(normalizeCause).filter((c) => c.label.trim());
  const root = causes.find((c) => c.id === record.rootId && c.state === ACTIVE);
  push(
    "CAUSES",
    causes
      .map((c) => {
        const head = `${GLYPH[c.state]} ${c.label.trim()}`;
        if (c.state === RULED_OUT) return `${head}${WHY_SEP}${c.why.trim()}`;
        if (c === root) return `${head}    <- ROOT CAUSE`;
        return head;
      })
      .join("\n")
  );
  push("STILL OPEN", cleanList(record.still_open).map((s) => `- ${s}`).join("\n"));
  push("RESOLUTION", record.resolution);
  push("WHAT WE LEARNED", record.summary);
  return parts.join("\n\n");
}

/* ── small pieces ───────────────────────────────────────────────────────── */

const INPUT =
  "w-full min-w-0 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[13px] " +
  "text-[#253439] placeholder-gray-400 outline-none transition-colors " +
  "focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100";

const ICON_BTN =
  "rounded p-1 text-gray-300 outline-none transition-colors hover:text-rose-500 " +
  "focus-visible:ring-2 focus-visible:ring-cyan-300";

const ADD_BTN =
  "inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-[12px] font-medium " +
  "text-cyan-600 outline-none transition-colors hover:bg-cyan-50 " +
  "focus-visible:ring-2 focus-visible:ring-cyan-300";

/* The segmented tray the header's toggles share. Two of them sit side by side
   now (what to show, then how to show it), so the chrome is defined once —
   they have to be indistinguishable or the second one reads as a different
   kind of control. */
const SEG_TRAY =
  "inline-flex items-center rounded-lg border border-gray-200 bg-gray-100 p-0.5";
const segBtn = (on) =>
  "rounded-md px-2 py-0.5 text-[11px] font-medium outline-none transition-colors " +
  "focus-visible:ring-2 focus-visible:ring-cyan-300 " +
  (on ? "bg-white text-[#253439] shadow-sm" : "text-gray-500 hover:text-gray-700");

/** The two ways to look at the record. Order matters — Preview is the default. */
const MODES = [
  { value: "preview", label: "Preview", hint: "Read the whole record" },
  { value: "edit", label: "Edit", hint: "Change the record" },
];

const SectionLabel = ({ children, hint }) => (
  <div className="mb-1.5 flex items-baseline gap-2 min-w-0">
    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
      {children}
    </span>
    {hint && <span className="truncate text-[11px] text-gray-400">{hint}</span>}
  </div>
);

/** Copy without a dependency — same behaviour as TrailSteps' own button. */
function CopyCase({ text }) {
  const [state, setState] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = useCallback(async () => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        if (!ok) throw new Error("copy rejected");
      }
      setState("Copied");
    } catch {
      setState("Copy failed");
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setState(""), 1600);
  }, [text]);

  return (
    <button
      type="button"
      onClick={copy}
      disabled={!text}
      title={text ? "Copy the conversation record" : "Nothing to copy yet"}
      className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-gray-500 outline-none transition-colors hover:bg-gray-100 hover:text-cyan-700 focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:opacity-40"
    >
      <Copy size={12} />
      <span>{state || "Copy conversation"}</span>
    </button>
  );
}

/** One short line in the symptoms / facts lists. */
function LineList({
  items,
  label,
  singular,
  readOnly,
  dot,
  idPrefix,
  focusKey,
  onFocused,
  onChange,
  onRemove,
  onAdd,
  onBlur,
  placeholder,
}) {
  if (readOnly) {
    return items.length ? (
      <ul className="space-y-1">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2 min-w-0 text-[13px] text-[#253439]">
            <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${dot}`} />
            <span className="min-w-0 break-words">{t}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-[13px] text-gray-400">Not filled in.</p>
    );
  }

  return (
    <div className="space-y-1">
      {items.map((t, i) => (
        <div key={i} className="flex items-center gap-2 min-w-0">
          <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${dot}`} aria-hidden="true" />
          <input
            id={`${idPrefix}-${i}`}
            className={INPUT}
            value={t}
            placeholder={placeholder}
            aria-label={`${singular} ${i + 1}`}
            ref={(el) => {
              if (el && focusKey === i) {
                el.focus();
                onFocused?.();
              }
            }}
            onChange={(e) => onChange(i, e.target.value)}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAdd(i + 1);
              }
            }}
          />
          <button
            type="button"
            className={`${ICON_BTN} flex-shrink-0`}
            aria-label={`Remove ${singular.toLowerCase()} ${i + 1}`}
            onClick={() => onRemove(i)}
          >
            <X size={13} />
          </button>
        </div>
      ))}
      <button type="button" className={ADD_BTN} onClick={() => onAdd(items.length)}>
        <Plus size={12} />
        <span>Add {singular.toLowerCase()}</span>
      </button>
      <span className="sr-only">{label}</span>
    </div>
  );
}

/** One cause: its state, its label, its reason, and whether it is the root. */
function CauseRow({ cause, index, isRoot, readOnly, muted, autoFocus, onState, onLabel, onWhy, onRoot, onRemove, onBlur }) {
  const whyOpen = cause.askWhy || cause.state === RULED_OUT;
  const meta = CAUSE_STATES.find((s) => s.value === cause.state);
  const name = cause.label.trim() || `cause ${index + 1}`;

  if (readOnly) {
    return (
      <li className="flex items-start gap-2 min-w-0 py-0.5 text-[13px]">
        <span className={`mt-px flex-shrink-0 rounded px-1 py-0.5 ${meta.on}`} title={meta.label}>
          <meta.Icon size={12} />
          {/* The glyph carries the state; say it out loud for anything that
              cannot see a rose X — including the tests. */}
          <span className="sr-only">{meta.short}</span>
        </span>
        <span className={`min-w-0 break-words ${muted ? "text-gray-500" : "text-[#253439]"}`}>
          {cause.label || <span className="text-gray-300">—</span>}
          {cause.state === RULED_OUT && cause.why && (
            <span className={muted ? "text-gray-400" : "text-gray-500"}> — {cause.why}</span>
          )}
        </span>
        {isRoot && (
          <span className="flex-shrink-0 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold text-cyan-700">
            Root cause
          </span>
        )}
      </li>
    );
  }

  return (
    <li
      className={`rounded-lg border px-2 py-1.5 transition-colors ${
        isRoot ? "border-cyan-300 bg-cyan-50/40" : "border-[#EBECE6] bg-white"
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex flex-shrink-0 items-center gap-0.5" role="group" aria-label={`State of ${name}`}>
          {CAUSE_STATES.map((s) => {
            const on = cause.state === s.value;
            return (
              <button
                key={s.value}
                type="button"
                aria-pressed={on}
                aria-label={`${s.short}: ${name}`}
                title={s.label}
                className={`rounded p-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-cyan-300 ${
                  on ? s.on : "text-gray-300 hover:text-gray-500"
                }`}
                onClick={() => onState(s.value)}
              >
                <s.Icon size={13} />
              </button>
            );
          })}
        </div>

        <input
          className={INPUT}
          value={cause.label}
          placeholder="A possible cause, in a few words"
          aria-label={`Cause ${index + 1}`}
          autoFocus={autoFocus}
          onChange={(e) => onLabel(e.target.value)}
          onBlur={onBlur}
        />

        {cause.nid ? (
          <span
            className="hidden flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-400 sm:inline"
            title="Runbook node id — a root cause picked here is countable"
          >
            {cause.nid}
          </span>
        ) : null}

        {isRoot ? (
          <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-cyan-500 px-2 py-0.5 text-[10px] font-semibold text-white">
            <Crown size={10} />
            Root cause
          </span>
        ) : (
          cause.state === ACTIVE && (
            <button
              type="button"
              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium text-gray-400 outline-none transition-colors hover:bg-cyan-50 hover:text-cyan-700 focus-visible:ring-2 focus-visible:ring-cyan-300"
              aria-label={`Make ${name} the root cause`}
              title="Make this the root cause"
              onClick={onRoot}
            >
              <Crown size={10} />
              Root cause
            </button>
          )
        )}

        <button
          type="button"
          className={`${ICON_BTN} flex-shrink-0`}
          aria-label={`Remove ${name}`}
          onClick={onRemove}
        >
          <X size={13} />
        </button>
      </div>

      {whyOpen && (
        <div className="mt-1.5 flex items-center gap-2 pl-14 min-w-0">
          <input
            className={`${INPUT} text-[12px]`}
            value={cause.why}
            placeholder="Why was it ruled out? one line"
            aria-label={`Why ${name} was ruled out`}
            autoFocus={cause.askWhy && !cause.why}
            onChange={(e) => onWhy(e.target.value)}
            onBlur={onBlur}
          />
        </div>
      )}
      {whyOpen && !cause.why.trim() && (
        <p className="mt-1 pl-14 text-[11px] text-amber-600">
          No reason yet, so this stays <strong>active</strong> — a cause nobody tested
          is still in play, not ruled out.
        </p>
      )}
    </li>
  );
}

/* ── informational entries ──────────────────────────────────────────────
   N self-contained learnings from one session, rendered as an expandable
   list. First entry expanded, the rest collapsed. */

/** Format a turn range: [1,2] → "T1–T2", [3] → "T3". */

/** Detail zone: procedure / key_points / options, plus optional verification. */
function EntryDetailZone({ entry }) {
  const proc = Array.isArray(entry.procedure) ? entry.procedure.filter(Boolean) : [];
  const kp = cleanList(entry.key_points || []);
  const opts = Array.isArray(entry.options) ? entry.options.filter(Boolean) : [];
  const veri = cleanList(entry.verification || []);

  const hasDetail = proc.length > 0 || kp.length > 0 || opts.length > 0;
  if (!hasDetail && !veri.length) return null;

  const zoneLabel = proc.length ? "How" : opts.length ? "Options" : kp.length ? "Key points" : null;

  return (
    <>
      {zoneLabel && <p className="info-zone-label">{zoneLabel}</p>}

      {proc.length > 0 && (
        <ol className="info-procedure">
          {proc.map((step, i) => (
            <li key={step.n || i} className="info-procedure-step">
              <span className="info-step-num">{step.n || i + 1}</span>
              <span className="info-step-body">
                {step.action}
                {step.note && <span className="info-step-note">{step.note}</span>}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* zoneLabel is one-of (How > Options > Key points); when it took another slot, key points still need their own label */}
      {kp.length > 0 && zoneLabel !== "Key points" && <p className="info-zone-label">Key points</p>}

      {kp.length > 0 && (
        <ul className="info-key-points">
          {kp.map((pt, i) => (
            <li key={i} className="info-key-point">
              <span className="info-key-point-dot" />
              <span>{pt}</span>
            </li>
          ))}
        </ul>
      )}

      {opts.length > 0 && (
        <div className="info-options">
          {opts.map((opt, i) => (
            <div key={i} className="info-option-card">
              <p className="info-option-name">{opt.option}</p>
              <div className="info-option-row">
                {opt.when_to_use && (
                  <div className="info-option-when">
                    <p className="info-option-label">When to use</p>
                    <p>{opt.when_to_use}</p>
                  </div>
                )}
                {opt.tradeoff && (
                  <div className="info-option-tradeoff">
                    <p className="info-option-label">Trade-off</p>
                    <p>{opt.tradeoff}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {veri.length > 0 && (
        <>
          <p className="info-zone-label" style={{ marginTop: 8 }}>Verification</p>
          <ul className="info-verification">
            {veri.map((v, i) => (
              <li key={i} className="info-verify-item">
                <CheckCircle2 size={14} className="info-verify-icon" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}

/** GOOD TO KNOW block: applies_to, prerequisites, caveats. Only when non-empty. */
function EntryGoodToKnow({ gtk }) {
  if (!gtk) return null;
  const at = cleanList(gtk.applies_to || []);
  const pre = cleanList(gtk.prerequisites || []);
  const cav = cleanList(gtk.caveats || []);
  if (!at.length && !pre.length && !cav.length) return null;

  return (
    <div className="info-gtk">
      <p className="info-gtk-label">Good to know</p>
      {(at.length > 0 || pre.length > 0) && (
        <div className="info-gtk-row">
          {at.length > 0 && (
            <span>
              <span className="info-gtk-pair-label">Applies to </span>
              <span className="info-gtk-pair-value">{at.join(" · ")}</span>
            </span>
          )}
          {pre.length > 0 && (
            <span>
              <span className="info-gtk-pair-label">Needs </span>
              <span className="info-gtk-pair-value">{pre.join(" · ")}</span>
            </span>
          )}
        </div>
      )}
      {cav.length > 0 && (
        <ul className="info-caveats info-gtk-caveats">
          {cav.map((c, i) => (
            <li key={i} className="info-caveat-item">
              <AlertTriangle size={14} className="info-caveat-icon" />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Footer line: sources + "Asked as ...". */
function EntryFooter({ sources, askedAs }) {
  const srcs = Array.isArray(sources) ? sources.filter(Boolean) : [];
  if (!srcs.length && !askedAs) return null;
  return (
    <div className="info-entry-footer">
      {srcs.map((src, i) => (
        <span key={i} className="info-source-chip">
          <span className={`info-source-kind info-source-kind--${src.kind || "doc"}`}>
            {src.kind || "doc"}
          </span>
          {src.ref && <span className="info-source-ref">{src.ref}</span>}
          {src.title && <span className="info-source-title">{src.title}</span>}
        </span>
      ))}
      {srcs.length > 0 && askedAs && <span style={{ color: "#d1d5db" }}>&middot;</span>}
      {askedAs && <span className="info-asked-as">Asked as &ldquo;{askedAs}&rdquo;</span>}
    </div>
  );
}

/** One entry card — expanded or collapsed. */
function EntryCard({ entry, index, expanded, onToggle }) {
  const kindLabel = (entry.kind || "").replace(/_/g, "-");

  return (
    <div className="info-entry">
      <div
        className="info-entry-collapsed"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      >
        <ChevronRight
          size={12}
          className={`info-entry-chevron${expanded ? " info-entry-chevron--open" : ""}`}
        />
        <span className="info-entry-num">{index + 1}</span>
        <span className="info-entry-title">{entry.title || "Untitled"}</span>
        {kindLabel && <span className="info-entry-kind">{kindLabel}</span>}
        {entry.meta?.basis === "suggested_check" && (
          <span
            className="info-entry-kind info-entry-kind--suggested"
            title="The assistant suggested this check for a candidate cause; the engineer did not report running it."
          >
            suggested check &middot; not performed
          </span>
        )}
      </div>

      {expanded && (
        <div className="info-entry-body">
          {entry.lead && <p className="info-entry-lead">{entry.lead}</p>}
          {entry.corrected && (
            <p className="info-corrected">
              <AlertTriangle size={12} style={{ flexShrink: 0 }} />
              <span>
                {entry.corrected.turn != null ? `Corrected in T${entry.corrected.turn}` : "Corrected later"}
                {entry.corrected.was && (
                  <> &mdash; was: &ldquo;<span className="info-corrected-was">{entry.corrected.was}</span>&rdquo;</>
                )}
                {entry.corrected.why && (
                  <> &middot; why: {entry.corrected.why}</>
                )}
              </span>
            </p>
          )}
          <EntryDetailZone entry={entry} />
          <EntryGoodToKnow gtk={entry.good_to_know} />
          <EntryFooter sources={entry.sources} askedAs={entry.asked_as} />
        </div>
      )}
    </div>
  );
}

/**
 * The entries list — "N things learned in this session" plus the accordions.
 *
 * Its own component because it no longer lives inside the Overview card: the
 * overlay renders it BELOW the turn breakdown, while the review screen still
 * renders it under the synthesis blocks. Both hand it the same thing, so it
 * normalises what it is given rather than trusting the caller — `entries` is
 * already normalised coming from a record, and raw coming from a trail.
 *
 * `heading` — when given (the overlay), the list is a SECTION of its own, laid
 * out like TurnBreakdown above it: a heading row outside (bold title, grey
 * count, Expand all / Collapse all on the right) and one white card with the
 * rows only — the heading carries the count, so the "N things learned" line
 * is dropped. Without it (TrailReview, inside CaseRecord's card) the list is
 * what it always was.
 */
export function LearningEntries({ entries, lead = null, heading = null }) {
  const rows = useMemo(
    () =>
      (Array.isArray(entries) ? entries : safeJson(entries, []) || [])
        .map(normalizeEntry)
        .filter(Boolean),
    [entries]
  );

  // First entry expanded by default, rest collapsed.
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    if (rows.length > 0) initial[rows[0].id || 0] = true;
    return initial;
  });

  const toggle = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  /* Expand all / Collapse all — TurnBreakdown's rule: anything collapsed
     means the control expands everything. */
  const anyCollapsed = rows.some((entry, i) => !expanded[entry.id || i]);
  const toggleAll = useCallback(() => {
    if (anyCollapsed) {
      const next = {};
      rows.forEach((entry, i) => { next[entry.id || i] = true; });
      setExpanded(next);
    } else {
      setExpanded({});
    }
  }, [anyCollapsed, rows]);

  if (!rows.length) return null;

  const items = rows.map((entry, i) => (
    <EntryCard
      key={entry.id || i}
      entry={entry}
      index={i}
      expanded={!!expanded[entry.id || i]}
      onToggle={() => toggle(entry.id || i)}
    />
  ));

  if (heading) {
    return (
      <section className="tb-root">
        <div className="tb-head">
          <h2 className="tb-heading">
            {heading}
            <span className="tb-heading-count">
              {rows.length} learning{rows.length > 1 ? "s" : ""}
            </span>
          </h2>
          {rows.length > 1 && (
            <button type="button" className="tb-expand-all" onClick={toggleAll}>
              {anyCollapsed ? "Expand all" : "Collapse all"}
            </button>
          )}
        </div>
        <div className="info-list-card">{items}</div>
      </section>
    );
  }

  return (
    <div className="min-w-0 space-y-3">
      {lead}
      <p className="info-header-line">
        {rows.length} thing{rows.length > 1 ? "s" : ""} learned in this session
      </p>
      {items}
    </div>
  );
}

/**
 * The full informational view: four synthesis blocks, and — unless the caller
 * has taken the list somewhere else (`showEntries: false`) — the entries list.
 */
function InformationalEntries({ record, editing, idPrefix, onEdit, onFlush, showEntries = true }) {
  const entries = record.entries || [];
  const about = asText(record.about).trim();
  const takeaways = cleanList(record.key_takeaways);
  const appliesTo = cleanList(record.applies_to);
  const validAsOf = asText(record.valid_as_of).trim();
  const watchOut = cleanList(record.watch_out);
  const stillOpen = cleanList(record.still_open);

  /* List editors for the synthesis fields. */
  const listChange = useCallback(
    (section, i, value) =>
      onEdit(section, (prev) => ({
        ...prev,
        [section]: prev[section].map((t, j) => (j === i ? value : t)),
      })),
    [onEdit]
  );
  const listRemove = useCallback(
    (section, i) =>
      onEdit(section, (prev) => ({
        ...prev,
        [section]: prev[section].filter((_, j) => j !== i),
      })),
    [onEdit]
  );
  const listAdd = useCallback(
    (section) => {
      onEdit(section, (prev) => ({
        ...prev,
        [section]: [...(prev[section] || []), ""],
      }));
    },
    [onEdit]
  );

  const hasGtk = appliesTo.length > 0 || validAsOf || watchOut.length > 0;
  const hasSynthesis = about || takeaways.length || hasGtk || stillOpen.length;

  return (
    <div className="min-w-0 space-y-3">

      {/* ── ABOUT ── */}
      {(about || editing) ? (
        <div className="min-w-0">
          <SectionLabel hint="what this session covered">About</SectionLabel>
          {!editing ? (
            <p className="whitespace-pre-wrap break-words text-[13px] text-[#253439]">{about}</p>
          ) : (
            <textarea
              id={`${idPrefix}-about`}
              className={`${INPUT} resize-y`}
              rows={1}
              value={record.about}
              placeholder="One line naming the territory this session covered..."
              aria-label="About"
              onChange={(e) => onEdit("about", (prev) => ({ ...prev, about: e.target.value }))}
              onBlur={onFlush}
            />
          )}
        </div>
      ) : null}

      {/* ── KEY TAKEAWAYS ── */}
      {(takeaways.length > 0 || editing) ? (
        <div className="min-w-0">
          <SectionLabel hint="what to remember">Key takeaways</SectionLabel>
          {!editing ? (
            <ul className="info-key-points">
              {takeaways.slice(0, 5).map((pt, i) => (
                <li key={i} className="info-key-point">
                  <span className="info-key-point-dot" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          ) : (
            <LineList
              items={record.key_takeaways || []}
              readOnly={false}
              label="Key takeaways"
              singular="Takeaway"
              dot="bg-blue-400"
              idPrefix={`${idPrefix}-kt`}
              placeholder="One key takeaway"
              onChange={(i, v) => listChange("key_takeaways", i, v)}
              onRemove={(i) => listRemove("key_takeaways", i)}
              onAdd={() => listAdd("key_takeaways")}
              onBlur={onFlush}
            />
          )}
        </div>
      ) : null}

      {/* ── GOOD TO KNOW ── */}
      {(hasGtk || editing) ? (
        <div className="min-w-0">
          <SectionLabel hint="scope and cautions">Good to know</SectionLabel>
          {!editing ? (
            <div className="info-gtk">
              {appliesTo.length > 0 && (
                <div className="info-gtk-subrow">
                  <span className="info-gtk-sublabel">Applies to</span>
                  <span className="info-gtk-subvalue">{appliesTo.join(" · ")}</span>
                </div>
              )}
              {validAsOf && (
                <div className="info-gtk-subrow">
                  <span className="info-gtk-sublabel">Valid as of</span>
                  <span className="info-gtk-subvalue">{validAsOf}</span>
                </div>
              )}
              {watchOut.length > 0 && (
                <div className="info-gtk-subrow info-gtk-subrow--warn">
                  <span className="info-gtk-sublabel">
                    <AlertTriangle size={11} style={{ display: "inline", verticalAlign: "-1px", marginRight: 3 }} />
                    Careful
                  </span>
                  <span className="info-gtk-subvalue">{watchOut.join(" · ")}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="min-w-0 space-y-2">
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 mb-1">Applies to</p>
                <LineList
                  items={record.applies_to || []}
                  readOnly={false}
                  label="Applies to"
                  singular="Tag"
                  dot="bg-cyan-400"
                  idPrefix={`${idPrefix}-at`}
                  placeholder="Product or context"
                  onChange={(i, v) => listChange("applies_to", i, v)}
                  onRemove={(i) => listRemove("applies_to", i)}
                  onAdd={() => listAdd("applies_to")}
                  onBlur={onFlush}
                />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 mb-1">Valid as of</p>
                <textarea
                  id={`${idPrefix}-vao`}
                  className={`${INPUT} resize-y`}
                  rows={1}
                  value={record.valid_as_of}
                  placeholder="e.g. ZCC 4.3.5 · Sep 2026"
                  aria-label="Valid as of"
                  onChange={(e) => onEdit("valid_as_of", (prev) => ({ ...prev, valid_as_of: e.target.value }))}
                  onBlur={onFlush}
                />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium text-gray-500 mb-1">Careful</p>
                <LineList
                  items={record.watch_out || []}
                  readOnly={false}
                  label="Careful"
                  singular="Warning"
                  dot="bg-amber-400"
                  idPrefix={`${idPrefix}-wo`}
                  placeholder="One thing to be careful about"
                  onChange={(i, v) => listChange("watch_out", i, v)}
                  onRemove={(i) => listRemove("watch_out", i)}
                  onAdd={() => listAdd("watch_out")}
                  onBlur={onFlush}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* ── STILL OPEN ── */}
      {(stillOpen.length > 0 || editing) ? (
        <div className="min-w-0">
          <SectionLabel hint="what we could not answer">Still open</SectionLabel>
          {!editing ? (
            <ul className="info-key-points">
              {stillOpen.map((pt, i) => (
                <li key={i} className="info-key-point">
                  <span className="info-key-point-dot" style={{ background: "#f59e0b" }} />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
          ) : (
            <LineList
              items={record.still_open || []}
              readOnly={false}
              label="Still open"
              singular="Question"
              dot="bg-amber-400"
              idPrefix={`${idPrefix}-so`}
              placeholder="A question that was not answered"
              onChange={(i, v) => listChange("still_open", i, v)}
              onRemove={(i) => listRemove("still_open", i)}
              onAdd={() => listAdd("still_open")}
              onBlur={onFlush}
            />
          )}
        </div>
      ) : null}

      {/* ── entries, when they still live in this card ──
          The rule is the one it always was: a divider only earns its line when
          there is something above it to divide from. */}
      {showEntries && (
        <LearningEntries
          entries={entries}
          lead={hasSynthesis ? <hr className="info-divider" /> : null}
        />
      )}
    </div>
  );
}

/* ── the component ──────────────────────────────────────────────────────── */

/**
 * Props:
 *   trail     — the trail object from GET /trails/{id}
 *   steps     — the trail's steps, used only for one-click cause suggestions
 *   readOnly  — render static text instead of inputs
 *   idPrefix  — makes input ids unique per screen
 *   onSave    — (patch) => Promise, the screen's PATCH helper
 *   onProblemChange — (text) => void, so the page header can follow the problem
 *   viewSwitch — optional { value, options: [{value,label,hint}], onChange,
 *                mode?, onModeChange? }. A second segmented control in the
 *                header, for a caller that shows two cards as one. Absent → no
 *                control, today's header. `onChange(view, modeOnScreen)`.
 *                With `onModeChange`, Preview/Edit is SHARED by the cards
 *                behind the switch: `mode` (when set) overrides this card's
 *                own, and a pick is reported back.
 *   showEntries — informational only: render the entries list inside the card.
 *                 False when the caller places the list itself.
 *
 * Ref: `{ flush() }` — save anything outstanding and report
 *      `{ status: "noop"|"saved"|"partial"|"pending"|"failed" }`.
 *      "pending" is a v2-only section (symptoms / facts) the backend has not
 *      learned yet: nothing was lost, so it does not block submitting.
 */
const CaseRecord = forwardRef(function CaseRecord(
  {
    trail,
    steps = [],
    readOnly = false,
    idPrefix = "cr",
    onSave,
    onProblemChange,
    problemFallback = true,
    viewSwitch = null,
    showEntries = true,
  },
  ref
) {
  const [record, setRecord] = useState(() => recordFromTrail(trail, { problemFallback }));
  /* "preview" | "edit". Preview is prose — the whole record readable at once,
     no input chrome and no inner scrollbars. Edit is the form. A record with
     nothing in it opens in Edit, because previewing nothing is useless. */
  const [mode, setMode] = useState(() =>
    isRecordEmpty(recordFromTrail(trail, { problemFallback })) ? "edit" : "preview"
  );
  /* ONE MODE FOR BOTH HALVES of a switched Overview card. The overlay mounts two
     CaseRecords behind one view switch, and each used to keep its own mode — so
     an informational session (its causal half empty, hence opened in Edit)
     flipped Preview -> Edit the moment "Causal Learning" was clicked. With a
     `viewSwitch.onModeChange`, the caller owns the mode once it is set (it is
     set on the first view switch, from the mode on screen, and on every pick);
     until then this card's own default applies. An empty record in Preview
     reads "Not filled in." on every field, which is what it should say. */
  const sharedMode =
    viewSwitch && typeof viewSwitch.onModeChange === "function"
      ? viewSwitch.mode || null
      : null;
  const shownMode = sharedMode || mode;
  // The engineer chose a mode by hand, so late extraction stops moving them.
  const modePickedRef = useRef(false);
  const [notice, setNotice] = useState({ tone: "error", message: "" });
  const [focusLine, setFocusLine] = useState(null); // "symptoms:2" | "facts:0"
  const [focusCause, setFocusCause] = useState(null);
  // Preview's ruled-out fold. `null` follows the default for the current list
  // length, so late extraction changes it; a click pins it either way.
  const [ruledOpen, setRuledOpen] = useState(null);

  // The live record, for the debounced save (which must not capture a stale one).
  const recordRef = useRef(record);
  recordRef.current = record;
  // The record as the server last had it: what the patch diffs against, so a
  // section's untouched keys never travel and never get an edit marker stamped.
  const baselineRef = useRef(record);
  // Sections the user actually touched. Cleared only on a confirmed save, so a
  // rejected PATCH never costs anybody what they typed.
  const dirtyRef = useRef(new Set());
  // Sections touched since this trail loaded; a superset of dirtyRef that a
  // save does NOT clear. See the same-trail merge below.
  const touchedRef = useRef(new Set());
  const saveTimer = useRef(null);
  // Bumped on every edit, so a save that lands after newer typing knows not to
  // mark those sections clean.
  const editSeq = useRef(0);
  // Saves run one at a time: two PATCHes in flight can land out of order and
  // the older one wins, which silently reverts the newer text.
  const chainRef = useRef(Promise.resolve());

  const trailId = trail?.session_pk ?? trail?.id ?? trail?.session_id ?? null;
  useEffect(() => {
    const next = recordFromTrail(trail, { problemFallback });
    setRecord(next);
    recordRef.current = next;
    baselineRef.current = next;
    dirtyRef.current = new Set();
    touchedRef.current = new Set();
    modePickedRef.current = false;
    setMode(isRecordEmpty(next) ? "edit" : "preview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailId]);

  /* The SAME trail arriving again — the extractor finishing after capture, or
     the background top-up. Sections the engineer has touched since load are
     theirs and are never overwritten (not even after a save: a response that
     was requested before the save would put the old text back). Untouched
     sections take the server's value, which is how late extraction shows up
     without the overlay being reopened. */
  const seenTrailRef = useRef(trail);
  useEffect(() => {
    if (seenTrailRef.current === trail) return;
    seenTrailRef.current = trail;
    if (!trail) return;
    const fresh = recordFromTrail(trail, { problemFallback });
    const touched = touchedRef.current;
    const pick = (section, keys) =>
      touched.has(section) ? {} : Object.fromEntries(keys.map((k) => [k, fresh[k]]));
    const merge = (base) => ({
      ...base,
      // ── diagnostic fields ──
      ...pick("problem", ["problem"]),
      ...pick("symptoms", ["symptoms"]),
      ...pick("facts", ["facts"]),
      ...(touched.has("causes") || touched.has("root")
        ? {}
        : { causes: fresh.causes, rootId: fresh.rootId, rootFallback: fresh.rootFallback }),
      ...pick("resolution", ["resolution"]),
      ...pick("summary", ["summary"]),
      // ── informational synthesis fields ──
      ...pick("about", ["about"]),
      ...pick("key_takeaways", ["key_takeaways"]),
      ...pick("applies_to", ["applies_to"]),
      ...pick("valid_as_of", ["valid_as_of"]),
      ...pick("watch_out", ["watch_out"]),
      ...pick("still_open", ["still_open"]),
      // entries are read-only (no edit UI), so always take the server's value.
      ...(touched.size ? {} : { entries: fresh.entries }),
    });
    const mergedBase = merge(baselineRef.current);
    baselineRef.current = mergedBase;
    setRecord((prev) => {
      const next = merge(prev);
      recordRef.current = next;
      return next;
    });
    /* The overlay opens mid-capture, so the record is usually empty — and
       empty opens in Edit. When extraction lands a moment later there IS
       something to read, so show it. Only while nothing has been touched and
       no mode was chosen by hand: nobody gets pulled out of a field they are
       typing in. */
    if (!modePickedRef.current && !touched.size && !isRecordEmpty(mergedBase)) {
      setMode("preview");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trail]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  /** Persist the dirty sections. Never throws. */
  const doSave = useCallback(async () => {
    clearTimeout(saveTimer.current);
    const sections = [...dirtyRef.current];
    if (!sections.length || readOnly || !onSaveRef.current) return { status: "noop" };
    const snapshot = recordRef.current;
    const patch = recordToPatch(snapshot, sections, baselineRef.current);

    /* Anything typed WHILE the request was in flight is not in this patch, so
       its section stays dirty — the keystroke that changed it also rescheduled
       the debounce, which will send the newer text. */
    const seq = editSeq.current;
    const clear = () => {
      if (seq !== editSeq.current) return;
      sections.forEach((s) => dirtyRef.current.delete(s));
      // What just landed is now what the server has, so the next patch diffs
      // against it and does not resend (and re-stamp) the same keys.
      baselineRef.current = snapshot;
    };

    // Nothing in the touched sections actually differs from what was loaded —
    // typed and retyped the same text, or an edit that only moved keys another
    // section owns. Sending an empty PATCH would stamp nothing but cost a round
    // trip; the sections are clean either way.
    if (!Object.keys(patch).length) {
      clear();
      return { status: "noop" };
    }
    try {
      await onSaveRef.current(patch);
      clear();
      setNotice({ tone: "error", message: "" });
      return { status: "saved" };
    } catch (e) {
      // The extended columns may not be accepted yet. Retry with the keys this
      // backend has always taken so the engineer's text still lands somewhere —
      // the causes list round-trips through `possible_causes`, so both
      // states survive.
      const legacy = Object.fromEntries(
        Object.entries(patch).filter(([k]) => LEGACY_RECORD_KEYS.has(k))
      );
      if (Object.keys(legacy).length && Object.keys(legacy).length < Object.keys(patch).length) {
        try {
          await onSaveRef.current(legacy);
          setNotice({
            tone: "warn",
            message:
              "Saved. The server has not learned the new conversation fields yet, so this trail is storing them in the old columns — nothing was lost.",
          });
          clear();
          return { status: "partial" };
        } catch {
          /* fall through to the original failure */
        }
      }
      if (!Object.keys(legacy).length) {
        // Fields with no legacy column to fall back on: symptoms/facts for
        // diagnostic, all six synthesis fields for informational. Pending,
        // not broken: the section stays dirty and the next save retries it.
        const isInfo = recordRef.current?.intent === "informational";
        setNotice({
          tone: "warn",
          message: isInfo
            ? "The server does not accept informational fields yet. Everything you typed is still on screen, and it will save once that ships."
            : "The server does not accept symptoms and facts yet, so those two lists are not stored. Everything you typed is still on screen, and it will save once that ships.",
        });
        return { status: "pending" };
      }
      setNotice({
        tone: "error",
        message:
          (e instanceof Error && e.message) ||
          "Could not save your edits. Everything you typed is still here — try again.",
      });
      return { status: "failed" };
    }
  }, [readOnly]);

  /** The queued form — one request at a time, in the order they were asked for. */
  const save = useCallback(() => {
    const next = chainRef.current.then(doSave, doSave);
    // Keep the chain alive even if something above rejects unexpectedly.
    chainRef.current = next.catch(() => {});
    return next;
  }, [doSave]);

  const saveRef = useRef(save);
  saveRef.current = save;

  useImperativeHandle(ref, () => ({ flush: () => saveRef.current() }), []);

  /** Mark a section dirty, update the record, and schedule the write. */
  const edit = useCallback(
    (section, updater) => {
      dirtyRef.current.add(section);
      touchedRef.current.add(section);
      editSeq.current += 1;
      setRecord((prev) => {
        const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        recordRef.current = next;
        return next;
      });
      // List edits have no blur to hang a save on, so everything is debounced;
      // a blur just fires it early.
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveRef.current();
      }, 1000);
    },
    []
  );

  const flushNow = useCallback(() => {
    saveRef.current();
  }, []);

  /**
   * Switch between prose and the form.
   *
   * Nothing is lost on the way: every keystroke already lives in `record`, so
   * the half-typed field survives the inputs being replaced by text. What does
   * NOT survive is the blur those inputs never get — the debounced write and
   * the header's problem line both hang off it — so both are fired here.
   *
   * It does not mark anything dirty or touched. Opening the form is not an
   * edit, and counting it as one would freeze every section against the
   * extractor finishing.
   *
   * Clicking the ACTIVE "Edit" toggles back to Preview. The segmented control
   * always had both segments visible, but the active one was a no-op —
   * `setMode("edit")` when mode is already "edit" changes nothing — which
   * left the engineer stuck in Edit when they missed the adjacent button.
   * ONLY Edit toggles: clicking the active "Preview" stays in Preview (it is
   * the safe, read-only state; flipping into Edit on a stray click is not).
   * The pending save is flushed either way.
   */
  const pickMode = useCallback(
    (next) => {
      const resolved =
        next === "edit" && shownMode === "edit" ? "preview" : next;
      modePickedRef.current = true;
      setMode(resolved);
      if (viewSwitch && typeof viewSwitch.onModeChange === "function") {
        viewSwitch.onModeChange(resolved);
      }
      // Only when it was actually edited: the parent treats being told as the
      // engineer owning the header line from then on.
      if (touchedRef.current.has("problem")) onProblemChange?.(recordRef.current.problem);
      saveRef.current();
    },
    [onProblemChange, viewSwitch, shownMode]
  );

  /* ── problem / resolution / summary ── */
  const setText = useCallback(
    (section, key, value) => edit(section, (prev) => ({ ...prev, [key]: value })),
    [edit]
  );

  /* The page header echoes the problem line. Told on BLUR, not per keystroke:
     the parent re-renders the step list and the trace-flow graph, and paying
     for that on every character typed is what made the old shared-state
     version feel heavy on a long trail. */
  const problemBlur = useCallback(() => {
    onProblemChange?.(recordRef.current.problem);
    saveRef.current();
  }, [onProblemChange]);

  /* ── symptoms / facts ── */
  const listChange = useCallback(
    (section, i, value) =>
      edit(section, (prev) => ({
        ...prev,
        [section]: prev[section].map((t, j) => (j === i ? value : t)),
      })),
    [edit]
  );
  const listRemove = useCallback(
    (section, i) =>
      edit(section, (prev) => ({
        ...prev,
        [section]: prev[section].filter((_, j) => j !== i),
      })),
    [edit]
  );
  const listAdd = useCallback(
    (section, at) => {
      edit(section, (prev) => {
        const next = [...prev[section]];
        next.splice(at, 0, "");
        return { ...prev, [section]: next };
      });
      setFocusLine(`${section}:${at}`);
    },
    [edit]
  );

  /* ── causes ── */
  const mapCause = useCallback(
    (id, fn) =>
      edit("causes", (prev) => ({
        ...prev,
        causes: prev.causes.map((c) => (c.id === id ? fn(c) : c)),
      })),
    [edit]
  );

  const causeState = useCallback(
    (id, next) =>
      edit("causes", (prev) => {
        const causes = prev.causes.map((c) => (c.id === id ? applyCauseState(c, next) : c));
        // A cause that left `active` cannot still be the root cause.
        const root = causes.find((c) => c.id === prev.rootId);
        return { ...prev, causes, rootId: root && root.state === ACTIVE ? prev.rootId : null };
      }),
    [edit]
  );

  const causeWhy = useCallback(
    (id, text) => mapCause(id, (c) => setCauseWhy(c, text)),
    [mapCause]
  );
  const causeLabel = useCallback(
    (id, text) => mapCause(id, (c) => normalizeCause({ ...c, label: text })),
    [mapCause]
  );
  const causeRemove = useCallback(
    (id) =>
      edit("causes", (prev) => ({
        ...prev,
        causes: prev.causes.filter((c) => c.id !== id),
        rootId: prev.rootId === id ? null : prev.rootId,
      })),
    [edit]
  );
  const pickRoot = useCallback(
    (id) =>
      edit("root", (prev) => {
        const hit = prev.causes.find((c) => c.id === id);
        if (!hit || hit.state !== ACTIVE) return prev;
        // Picking or unpicking is the engineer overruling whatever was stored,
        // so the held-back stored root goes with it — unpicking really does
        // clear the field, it is not silently kept alive by the fallback.
        return { ...prev, rootId: prev.rootId === id ? null : id, rootFallback: null };
      }),
    [edit]
  );
  const causeAdd = useCallback(
    (seed = {}) => {
      const added = normalizeCause({ state: ACTIVE, ...seed });
      edit("causes", (prev) => ({ ...prev, causes: [...prev.causes, added] }));
      if (!seed.label) setFocusCause(added.id);
    },
    [edit]
  );

  /* The one derived flag the whole render hangs off. `readOnly` is the caller
     saying this trail cannot be edited at all — there is no toggle then, and
     no way back into the form. */
  const editing = !readOnly && shownMode === "edit";

  const suggestions = useMemo(
    () => (editing ? causeSuggestionsFromSteps(steps, record.causes || []) : []),
    [steps, record.causes, editing]
  );

  const rollup = useMemo(() => composeCaseText(record), [record]);
  const rootCause = (record.causes || []).find(
    (c) => c.id === record.rootId && c.state === ACTIVE
  );

  const lineProps = (section) => ({
    items: record[section],
    readOnly: !editing,
    idPrefix: `${idPrefix}-${section}`,
    focusKey:
      focusLine && focusLine.startsWith(`${section}:`)
        ? Number(focusLine.split(":")[1])
        : null,
    onFocused: () => setFocusLine(null),
    onChange: (i, v) => listChange(section, i, v),
    onRemove: (i) => listRemove(section, i),
    onAdd: (at) => listAdd(section, at),
    onBlur: flushNow,
  });

  /* Layout, on the step this codebase already uses (4 / 8 / 12 / 16 / 20px).
     ONE container owns the rhythm — 12px between every group, whether it is
     full width or half — so PROBLEM→SYMPTOMS→CAUSES→RESOLUTION are evenly
     spaced however the pairs fall out.

     Edit stacks. Preview is two columns, and a pair keeps its row only while
     the two sides are within a couple of lines of each other; past that it
     goes full width rather than leaving a tall hole under the short one. */
  const GROUPS = editing ? "min-w-0 space-y-3" : "grid min-w-0 gap-x-5 gap-y-3 md:grid-cols-2";
  const WIDE = editing ? "min-w-0" : "min-w-0 md:col-span-2";
  /* `md:contents` dissolves the wrapper so symptoms and facts become cells of
     the grid above and share its single gap. */
  const PAIR = editing
    ? "grid gap-3 min-w-0 md:grid-cols-2"
    : "min-w-0 space-y-3 md:contents md:space-y-0";
  const HALF = editing || pairsWell(record.symptoms, record.facts) ? "min-w-0" : WIDE;
  const TAIL = editing || pairsWell(record.resolution, record.summary) ? "min-w-0" : WIDE;

  /* One row renderer for both modes — `readOnly` is the only thing that
     differs, and the index is the row's place in the record so the aria
     labels stay stable however preview reorders them. */
  const causeRow = (c, muted = false) => {
    const i = record.causes.indexOf(c);
    return (
      <CauseRow
        key={c.id}
        cause={c}
        index={i}
        isRoot={c.id === record.rootId && c.state === ACTIVE}
        readOnly={!editing}
        muted={muted}
        autoFocus={focusCause === c.id}
        onState={(s) => causeState(c.id, s)}
        onLabel={(v) => causeLabel(c.id, v)}
        onWhy={(v) => causeWhy(c.id, v)}
        onRoot={() => pickRoot(c.id)}
        onRemove={() => causeRemove(c.id)}
        onBlur={flushNow}
      />
    );
  };

  /* Preview's two groups. The root cause leads the active list: it is the
     conclusion, and hunting for a pill in eight rows is not "at a glance". */
  const previewRuled = record.causes.filter((c) => c.state === RULED_OUT);
  const previewActive = record.causes
    .filter((c) => c.state !== RULED_OUT)
    .sort((a, b) => (a.id === record.rootId ? -1 : 0) - (b.id === record.rootId ? -1 : 0));
  /* One or two disproved rows on a short list cost nothing and reading them
     is free. Three of them, or a section already past eight rows — the live
     trail returns nine still in play — and they fold, so the active causes
     and the conclusion stay the thing you see. */
  const ruledOpenByDefault =
    previewRuled.length < 3 && previewActive.length + previewRuled.length <= 8;
  const showRuled = ruledOpen === null ? ruledOpenByDefault : ruledOpen;

  return (
    /* A white panel on the tab's grey, the way the turn cards below are —
       but the chrome pays for itself now: what went was the old card's
       compounding padding, not the surface. */
    <section className="mb-3 min-w-0 overflow-hidden rounded-xl border border-[#EBECE6] bg-white">
      {/* The header is a band, not bold text floating on the body: the tint
          is --arta-surface (#f9fafb), the same ground every other header in
          dashboard5.css sits on, closed by a hairline. */}
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 min-w-0 border-b border-[#EBECE6] bg-gray-50 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="min-w-0 truncate text-[13px] font-semibold text-[#253439]">
            Overview
          </h2>
          {/* The chip names the shape of the record. With a view switch in the
              header the switch already says that, and louder — two labels for
              one fact just crowd a narrow band. */}
          {record.intent === "informational" && !viewSwitch && (
            <span className="info-kind-chip">informational</span>
          )}
        </div>
        {/* A mode switch and a one-shot action are different kinds of
            control, so they do not share a cluster: the segmented toggle
            keeps its tray, then a hairline, then the action. */}
        <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5 min-w-0">
          {/* WHAT is shown, then HOW it is shown — in that order, because the
              second only makes sense once the first is settled. It is a
              labelled control rather than a click on the card: a whole-card
              hit area with no affordance is a feature nobody finds. */}
          {viewSwitch && viewSwitch.options.length > 1 && (
            <>
              <div className={SEG_TRAY} role="group" aria-label="Overview content">
                {viewSwitch.options.map((o) => {
                  const on = viewSwitch.value === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      aria-pressed={on}
                      title={o.hint}
                      className={segBtn(on)}
                      onClick={() => viewSwitch.onChange(o.value, shownMode)}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <span className="h-4 w-px bg-gray-200" aria-hidden="true" />
            </>
          )}
          {!readOnly && (
            <div className={SEG_TRAY} role="group" aria-label="Overview view">
              {MODES.map((m) => {
                const on = shownMode === m.value;
                return (
                  <button
                    key={m.value}
                    type="button"
                    aria-pressed={on}
                    title={m.hint}
                    className={segBtn(on)}
                    onClick={() => pickMode(m.value)}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
          )}
          {!readOnly && <span className="h-4 w-px bg-gray-200" aria-hidden="true" />}
          <CopyCase text={rollup} />
        </div>
      </div>

      {/* One padding, one rhythm. The body pads 12px and the groups space 12px;
          nothing adds a margin of its own on top of that. */}
      <div className="min-w-0 p-3">

      {notice.message && (
        <p
          className={`mb-2 rounded-lg px-2.5 py-1.5 text-[12px] ${
            notice.tone === "warn"
              ? "bg-amber-50 text-amber-700"
              : "bg-red-50 text-red-700"
          }`}
          role="status"
        >
          {notice.message}
        </p>
      )}

      {/* One rhythm for the whole block: 12px between groups, which is the
          step trail-steps.css already uses inside a case body. Preview lays
          the same six groups out two-up so the pane's width does some work —
          problem and causes span, the four short ones pair off. */}

      {record.intent === "informational" ? (
      /* ── INFORMATIONAL: synthesis + entries list ─────────────────────── */
      <InformationalEntries
        record={record}
        editing={editing}
        idPrefix={idPrefix}
        onEdit={edit}
        onFlush={flushNow}
        showEntries={showEntries}
      />
      ) : (
      /* ── DIAGNOSTIC layout (existing, unchanged) ─────────────────────── */
      <div className={GROUPS}>

      {/* ── problem ── */}
      <div className={WIDE}>
        <SectionLabel hint="what came in">Problem</SectionLabel>
        {!editing ? (
          <p
            className={`whitespace-pre-wrap break-words text-[13px] ${
              record.problem ? "text-[#253439]" : "text-gray-400"
            }`}
          >
            {record.problem || "Not filled in."}
          </p>
        ) : (
          <textarea
            id={`${idPrefix}-problem`}
            className={`${INPUT} resize-y`}
            rows={2}
            value={record.problem}
            placeholder="Who, where, when, what changed — not the cause and not the fix."
            aria-label="Problem"
            onChange={(e) => setText("problem", "problem", e.target.value)}
            onBlur={problemBlur}
          />
        )}
      </div>

      {/* ── symptoms / facts: deliberately two blocks ── */}
      {/* In preview this wrapper is `display: contents`, so the two lists are
          cells of the OUTER grid and line up with everything else rather than
          forming a nested grid of their own. */}
      <div className={PAIR}>
        <div className={HALF}>
          <SectionLabel hint="what you saw">Symptoms</SectionLabel>
          <LineList
            {...lineProps("symptoms")}
            label="Symptoms"
            singular="Symptom"
            dot="bg-cyan-400"
            placeholder="One observed behaviour"
          />
        </div>
        <div className={HALF}>
          <SectionLabel hint="what you were told">Facts</SectionLabel>
          <LineList
            {...lineProps("facts")}
            label="Facts"
            singular="Fact"
            dot="bg-violet-400"
            placeholder="One stated value"
          />
        </div>
      </div>

      {/* ── causes: ONE list, two states ── */}
      <div className={WIDE}>
        <SectionLabel hint="ruling one out needs a reason">Causes</SectionLabel>
        {record.causes.length === 0 ? (
          <p className="mb-1 text-[13px] text-gray-400">
            {!editing ? "Not filled in." : "No causes yet — add the ones that were considered."}
          </p>
        ) : editing ? (
          <ul className="space-y-1">{record.causes.map((c) => causeRow(c))}</ul>
        ) : (
          /* Preview groups by state instead of keeping the edit order. The
             reconciled read path can return eleven rows — eight still in
             play, two disproved and the conclusion — and interleaving them
             buries the answer. Active first, conclusion at the top of it;
             the disproved ones are history, so they go last, muted, and
             fold away once there are enough of them to bury anything. */
          <>
            <ul className="space-y-0.5">{previewActive.map((c) => causeRow(c))}</ul>
            {previewRuled.length > 0 && (
              <div className={previewActive.length ? "mt-1" : ""}>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] font-medium text-gray-400 outline-none transition-colors hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-cyan-300"
                  aria-expanded={showRuled}
                  onClick={() => setRuledOpen(!showRuled)}
                >
                  {showRuled ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span>
                    {previewRuled.length} ruled out
                  </span>
                </button>
                {showRuled && (
                  <ul className="space-y-0.5">
                    {previewRuled.map((c) => causeRow(c, true))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}

        {editing && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 min-w-0">
            <button type="button" className={ADD_BTN} onClick={() => causeAdd()}>
              <Plus size={12} />
              <span>Add a cause</span>
            </button>
            {suggestions.map((s) => (
              <button
                key={s.nid}
                type="button"
                title={`From the walk — ${s.nid}`}
                aria-label={`Add cause from the walk: ${s.label}`}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-[11px] text-gray-500 outline-none transition-colors hover:border-cyan-300 hover:text-cyan-700 focus-visible:ring-2 focus-visible:ring-cyan-300"
                onClick={() => causeAdd(s)}
              >
                <Plus size={10} className="flex-shrink-0" />
                <span className="truncate">{s.label}</span>
              </button>
            ))}
          </div>
        )}

        <p className="mt-1.5 text-[11px] text-gray-400">
          {rootCause ? (
            <>
              Root cause: <strong className="text-[#253439]">{rootCause.label.trim() || "—"}</strong>
              {rootCause.nid ? ` · ${rootCause.nid}` : " · typed in, no runbook node"}
            </>
          ) : editing ? (
            "No root cause picked. Pick one from the list — it is never typed."
          ) : (
            "No root cause picked."
          )}
        </p>
      </div>

      {/* ── still open: the asks nobody answered ──
          Job A writes this on diagnostic sessions, so it has to be readable
          here and not only in the informational layout. Hidden in preview
          when empty — "Not filled in." would read as a gap to fill, and an
          empty list here is the good outcome. */}
      {(cleanList(record.still_open).length > 0 || editing) && (
        <div className={WIDE}>
          <SectionLabel hint="what we could not answer">Still open</SectionLabel>
          <LineList
            {...lineProps("still_open")}
            items={editing ? record.still_open || [] : cleanList(record.still_open)}
            label="Still open"
            singular="Question"
            dot="bg-amber-400"
            placeholder="A question that was not answered"
          />
        </div>
      )}

      {/* ── resolution ── */}
      <div className={TAIL}>
        <SectionLabel hint="what ended it">Resolution</SectionLabel>
        {!editing ? (
          /* No rows, no inner scrollbar. The 2-row textarea clipped a long
             resolution mid-sentence and made the engineer scroll a box inside
             a scrolling pane to read their own case. */
          <p
            className={`whitespace-pre-wrap break-words text-[13px] ${
              record.resolution ? "text-[#253439]" : "text-gray-400"
            }`}
          >
            {record.resolution || "Not filled in."}
          </p>
        ) : (
          <textarea
            id={`${idPrefix}-resolution`}
            className={`${INPUT} resize-y`}
            rows={2}
            value={record.resolution}
            placeholder="The fix or the workaround that ended it..."
            aria-label="Resolution"
            onChange={(e) => setText("resolution", "resolution", e.target.value)}
            onBlur={flushNow}
          />
        )}
      </div>

      {/* ── what we learned (unchanged field, kept so nothing regresses) ── */}
      <div className={TAIL}>
        <SectionLabel>What we learned</SectionLabel>
        {!editing ? (
          <p
            className={`whitespace-pre-wrap break-words text-[13px] ${
              record.summary ? "text-[#253439]" : "text-gray-400"
            }`}
          >
            {record.summary || "Not filled in."}
          </p>
        ) : (
          <textarea
            id={`${idPrefix}-summary`}
            className={`${INPUT} resize-y`}
            rows={2}
            value={record.summary}
            placeholder="The insight an engineer should take away from this session..."
            aria-label="What we learned"
            onChange={(e) => setText("summary", "summary", e.target.value)}
            onBlur={flushNow}
          />
        )}
      </div>

      </div>
      )}

      </div>
    </section>
  );
});

export default CaseRecord;
